import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Document, EditorState } from '../types/editor';
import { documentDB } from '../utils/indexedDB';

interface EditorStore extends EditorState {
  createDocument: () => void;
  updateDocument: (id: string, content: string) => void;
  updateDocumentTitle: (id: string, title: string) => void;
  deleteDocument: (id: string) => void;
  setActiveDocument: (id: string) => void;
  toggleFullscreen: () => void;
  saveToLocalStorage: () => void;
}

const createNewDocument = (): Document => ({
  id: Date.now().toString(),
  title: 'Untitled',
  content: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  wordCount: 0,
  charCount: 0,
});

const calculateStats = (content: string) => {
  const words = content.trim() === '' ? 0 : content.trim().split(/\s+/).length;
  const chars = content.length;
  return { wordCount: words, charCount: chars };
};

export const useEditorStore = create<EditorStore>()(
  persist(
    (set, get) => ({
      activeDocument: null,
      documents: [],
      isFullscreen: false,
      isAutoSaving: false,
      lastSaved: null,

      createDocument: () => {
        const newDoc = createNewDocument();
        set((state) => ({
          documents: [...state.documents, newDoc],
          activeDocument: newDoc,
        }));
      },

      updateDocument: (id, content) => {
        const stats = calculateStats(content);
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === id
              ? {
                  ...doc,
                  content,
                  updatedAt: new Date(),
                  ...stats,
                }
              : doc
          ),
          activeDocument:
            state.activeDocument?.id === id
              ? {
                  ...state.activeDocument,
                  content,
                  updatedAt: new Date(),
                  ...stats,
                }
              : state.activeDocument,
        }));
      },

      updateDocumentTitle: (id, title) => {
        set((state) => ({
          documents: state.documents.map((doc) =>
            doc.id === id
              ? {
                  ...doc,
                  title: title.trim() || 'Untitled',
                  updatedAt: new Date(),
                }
              : doc
          ),
          activeDocument:
            state.activeDocument?.id === id
              ? {
                  ...state.activeDocument,
                  title: title.trim() || 'Untitled',
                  updatedAt: new Date(),
                }
              : state.activeDocument,
        }));
      },

      deleteDocument: (id) => {
        set((state) => {
          const newDocs = state.documents.filter((doc) => doc.id !== id);
          return {
            documents: newDocs,
            activeDocument:
              state.activeDocument?.id === id
                ? newDocs[0] || null
                : state.activeDocument,
          };
        });
      },

      setActiveDocument: (id) => {
        set((state) => ({
          activeDocument:
            state.documents.find((doc) => doc.id === id) || null,
        }));
      },

      toggleFullscreen: () => {
        set((state) => ({ isFullscreen: !state.isFullscreen }));
      },

      saveToLocalStorage: () => {
        const state = get();
        if (state.activeDocument) {
          // IndexedDB에도 백업
          documentDB.saveDocument(state.activeDocument).catch(console.error);
        }
        set({ lastSaved: new Date() });
      },
    }),
    {
      name: 'focuswriter-editor',
    }
  )
);